"use client";

import { ArrowLeft, RotateCcw } from "lucide-react";
import { useErrorRecovery } from "@/hooks/useErrorRecovery";

// Recuperação automática antes da tela de erro (lib/runtime/errorRecovery.ts):
// erro de carregamento de código/rede (aba aberta antes de um deploy, rede
// instável) -> um recarregamento completo; outros erros -> uma nova
// tentativa após um pequeno atraso. A tela abaixo continua sendo a resposta
// final quando a recuperação automática não resolve.
export default function ErrorPage({
  error,
  reset,
  retry,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  retry?: () => void;
}) {
  const tryAgain = retry ?? reset;
  const recovering = useErrorRecovery(error, tryAgain, "[storefront-render-error]");

  if (recovering) {
    return (
      <main id="main-content" className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4 py-12">
        <p role="status" aria-live="polite" className="text-muted">
          Atualizando a página…
        </p>
      </main>
    );
  }

  return (
    <main id="main-content" className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4 py-12">
      <section className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8">
        <h1 className="text-2xl font-bold text-heading">
          Não foi possível carregar esta página
        </h1>
        <p className="mt-3 text-muted">
          Houve uma falha temporária ao consultar a loja. Você pode tentar novamente sem perder sua navegação.
        </p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={tryAgain}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-secondary px-5 font-semibold text-white hover:bg-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <RotateCcw className="h-5 w-5" aria-hidden="true" />
            Tentar novamente
          </button>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-primary px-5 font-semibold text-primary hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            Voltar
          </button>
        </div>
        {error.digest ? (
          <p className="mt-5 text-xs text-muted">Código: {error.digest}</p>
        ) : null}
      </section>
    </main>
  );
}
